import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useCompany, useUI } from '../contexts';
import { recordEvent } from '../functions';
import '../styles/BackToClaudeWidget.css';

// Floating nudge shown ONLY to users whose workspace was created from inside
// Claude (company doc has signupSource === 'claude', stamped by the MCP
// connector's create_workspace tool). These users live in Claude — when they
// land in the web app we offer a one-tap way back: a copy-paste prompt and a
// desktop deep link. Web-origin accounts never have the flag, so they never
// see this. Pinned bottom-LEFT to clear the bottom-right setup checklist.

// What we drop them back into. Not the onboarding prompt — they're already set
// up; this is the "keep working" prompt that surfaces their freshest deals.
const RETURN_PROMPT = 'Show me my newest Clozers deals';
// The desktop app still honours claude://claude.ai/new?q= (the web
// claude.ai/new?q= was removed Oct 2025), so this link only helps desktop users
// — offered as a bonus next to the always-works copy button.
const DESKTOP_LINK = `claude://claude.ai/new?q=${encodeURIComponent(RETURN_PROMPT)}`;

// Routes where the nudge shouldn't appear: auth/onboarding (mid-setup) — match
// the setup-checklist widget so the two floating nudges stay consistent.
const HIDDEN_PREFIXES = ['/onboarding', '/sign-up', '/login', '/company-setup', '/connect-ai'];
const isHiddenRoute = (pathname) =>
    HIDDEN_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));

const dismissKey = (companyId) => `clozers:backToClaudeDismissed:${companyId || 'unknown'}`;

function BackToClaudeWidget() {
    const location = useLocation();
    const { companyData, companyId } = useCompany();
    const { showDarkBackground } = useUI();

    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    // Bumping this state forces a re-render after dismissal; the actual
    // "dismissed?" answer is read from localStorage below, keyed on the loaded
    // companyId. (A one-shot initializer would run before companyId hydrates and
    // miss a prior dismissal, resurfacing the widget every session.)
    const [, forceRender] = useState(0);

    let dismissed = false;
    try { dismissed = localStorage.getItem(dismissKey(companyId)) === '1'; } catch (_) { /* ignore */ }

    const isClaudeOrigin = companyData?.signupSource === 'claude';
    // Defer to any open modal/overlay (app-wide signal), like the setup widget.
    const visible = isClaudeOrigin && !dismissed
        && !isHiddenRoute(location.pathname)
        && !showDarkBackground;

    // One-time impression once the nudge actually becomes visible, so the
    // interaction events (toggled/copied/dismissed) have a denominator: what
    // share of Claude-origin users who SEE the nudge act on it. Ref-guarded so
    // route changes / re-renders don't re-fire it within a mount.
    const shownRef = useRef(false);
    useEffect(() => {
        if (visible && !shownRef.current) {
            shownRef.current = true;
            recordEvent('back_to_claude_shown', { companyId });
        }
    }, [visible, companyId]);

    if (!visible) return null;

    const handleDismiss = () => {
        try { localStorage.setItem(dismissKey(companyId), '1'); } catch (_) { /* non-fatal */ }
        forceRender((n) => n + 1);
        recordEvent('back_to_claude_dismissed', { companyId });
    };

    const handleCopy = () => {
        navigator.clipboard?.writeText(RETURN_PROMPT).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }).catch(() => {});
        recordEvent('back_to_claude_copied', { companyId });
    };

    const handleToggle = () => {
        const next = !open;
        setOpen(next);
        recordEvent('back_to_claude_toggled', { open: next, companyId });
    };

    return (
        <div className="btc-root">
            {open && (
                <div className="btc-card" role="dialog" aria-label="Get back to Claude">
                    <div className="btc-card-head">
                        <span className="body-semibold colour-primary">Back to Claude</span>
                        <button
                            type="button"
                            className="btc-close cursor-pointer"
                            onClick={handleDismiss}
                            aria-label="Dismiss"
                        >
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                        </button>
                    </div>
                    <span className="label-regular colour-secondary">
                        You set up in Claude — pick up where you left off. Send Claude this message:
                    </span>
                    <div className="btc-prompt">
                        <span className="body-regular colour-primary btc-prompt-text">{RETURN_PROMPT}</span>
                        <button
                            type="button"
                            className="btc-copy-button label-bold cursor-pointer"
                            onClick={handleCopy}
                        >
                            {copied ? 'Copied' : 'Copy'}
                        </button>
                    </div>
                    <a
                        className="btc-secondary label-bold"
                        href={DESKTOP_LINK}
                        onClick={() => recordEvent('back_to_claude_opened_desktop', { companyId })}
                    >
                        Or open in the Claude desktop app →
                    </a>
                </div>
            )}

            <button
                type="button"
                className="btc-pill cursor-pointer"
                onClick={handleToggle}
                aria-expanded={open}
            >
                <span className="body-semibold colour-primary">Back to Claude</span>
                <svg
                    className={`btc-pill-chevron${open ? ' btc-pill-chevron--open' : ''}`}
                    width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"
                >
                    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
        </div>
    );
}

export default BackToClaudeWidget;
